<script setup lang="ts">
import type { ITableColumnsOptions } from '@modules/tables/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import {
  AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS_PERMISSIONS,
  TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS,
} from '@modules/affiliate-program-subaffiliate-deals/constants';
import { useAffiliateProgramSubAffiliateDealsListQuery } from '@modules/affiliate-program-subaffiliate-deals/queries';
import { AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS_EDIT_ROUTE_NAME } from '@modules/affiliate-program-subaffiliate-deals/submodules/edit/constants';
import { useAffiliateProgramSubAffiliateDealDeleteMutation } from '@modules/affiliate-program-subaffiliate-deals/submodules/edit/mutations';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import { useI18n } from '@modules/i18n/composables';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';

const {
  tableState,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(useAffiliateProgramSubAffiliateDealsListQuery);

const { confirmDelete } = useConfirmDialogs();
const { getTranslation } = useI18n();
const { mutate: deleteDeal } = useAffiliateProgramSubAffiliateDealDeleteMutation();

const columnOptions: ITableColumnsOptions = {
  title: {
    defaultMobilePreview: true,
  },
};

function onDelete(event: Event, id: number): void {
  confirmDelete({
    event,
    header: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS, 'DELETE_MODAL_REMOVE').value,
    message: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS, 'DELETE_MODAL_REMOVE_QUESTION').value,
    acceptLabel: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS, 'DELETE_MODAL_DELETE').value,
    rejectLabel: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS, 'DELETE_MODAL_CANCEL').value,
    accept: () => {
      deleteDeal(id);
    },
  });
}
</script>

<template>
  <VCard v-data-qa-auto-id="'AffiliateProgramSubAffiliateDealsListPage'" type="table">
    <VTable
      :data="data"
      :state="tableState"
      :loading="isLoading"
      :is-fetching="isFetching"
      :title-t-path="$getTPath(TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS, 'HEADER')"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VIconButton
          v-if="$hasPermission(AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS_PERMISSIONS.EDIT)"
          :icon="listItem.read_only ? 'pi-eye' : 'pi-file-edit'"
          :to="{
            name: AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS_EDIT_ROUTE_NAME,
            params: {
              id: String(listItem.id),
            },
          }"
        />
        <VIconButton
          v-if="!listItem.read_only && $hasPermission(AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS_PERMISSIONS.DELETE)"
          icon="pi-trash"
          @click="onDelete($event, listItem.id)"
        />
      </template>
      <template
        v-if="$hasPermission(AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS_PERMISSIONS.EDIT)"
        #footerButtons
      >
        <VButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS, 'CREATE_PLAN')"
          :to="{ name: AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS_EDIT_ROUTE_NAME }"
        />
      </template>
    </VTable>
  </VCard>
</template>
